/**
 * MarIA Health Care - Encuesta de Salud Integral
 *
 * Schema SurveyJS para la encuesta de salud de la mujer.
 *
 * VERSIÓN: 3.0.0 (S.I.F.O.N. C3 Fix - Investigación Completa)
 * FECHA: 2026-01-20
 *
 * CAMBIOS v3.0.0:
 * - 77 campos completos alineados con encuesta.py
 * - PHQ-4 completo (4 ítems validados científicamente)
 * - Cribados oncológicos (mamografía, papanicolaou)
 * - Sección medicamentos
 * - Sección estudios (condicional para estudiantes)
 * - Sección hobbies completa
 * - Sección tecnologías de salud
 * - Sección empoderamiento completa (11 campos)
 * - Hábitos alimentarios completos (7 campos)
 *
 * Propósitos:
 * 1. Investigación universitaria - Universidad de Murcia (datos completos)
 * 2. Personalización de MarIA - Perfil para respuestas contextualizadas
 *
 * @see encuesta.py - Fuente de verdad para campos
 * @see https://surveyjs.io/form-library/documentation
 */

export const surveyJson = {
  title: "Tu Perfil de Salud",
  description: "Esta información nos ayudará a personalizar MarIA para ti y contribuirá a la investigación sobre salud de la mujer.",
  logo: "",
  logoPosition: "right",

  showProgressBar: "top",
  progressBarType: "questions",
  progressBarShowPageNumbers: true,
  progressBarShowPageTitles: true,

  showNavigationButtons: "bottom",
  showPrevButton: true,
  showQuestionNumbers: "off",
  questionErrorLocation: "bottom",

  sendResultOnPageNext: true,

  firstPageIsStarted: true,
  startSurveyText: "Comenzar",

  showCompletedPage: true,
  completedHtml: `
    <div class="text-center py-8">
      <div class="text-6xl mb-4">🎉</div>
      <h2 class="text-2xl font-bold text-primary mb-2">¡Gracias por completar tu perfil!</h2>
      <p class="text-muted-foreground mb-4">
        Tus respuestas nos ayudarán a personalizar MarIA para ti.
      </p>
      <p class="text-sm text-muted-foreground">
        Puedes actualizar tu perfil en cualquier momento desde el menú "Mi Salud".
      </p>
    </div>
  `,

  locale: "es",

  pages: [
    // =========================================================================
    // START PAGE - Introducción
    // =========================================================================
    {
      name: "intro",
      elements: [
        {
          type: "html",
          name: "intro_text",
          html: `
            <div class="text-center py-4">
              <div class="text-5xl mb-4">👋</div>
              <h2 class="text-2xl font-semibold mb-4">Bienvenida a tu Perfil de Salud</h2>
              <p class="text-muted-foreground mb-6 max-w-md mx-auto">
                En los próximos minutos te haré algunas preguntas para conocerte mejor
                y poder ofrecerte información personalizada sobre tu salud.
              </p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-sm">
                <div class="p-4 bg-secondary/50 rounded-lg">
                  <div class="text-2xl mb-2">⏱️</div>
                  <div class="font-medium">~15 minutos</div>
                  <div class="text-muted-foreground">Tiempo estimado</div>
                </div>
                <div class="p-4 bg-secondary/50 rounded-lg">
                  <div class="text-2xl mb-2">💾</div>
                  <div class="font-medium">Guardado auto</div>
                  <div class="text-muted-foreground">Puedes pausar</div>
                </div>
                <div class="p-4 bg-secondary/50 rounded-lg">
                  <div class="text-2xl mb-2">🔒</div>
                  <div class="font-medium">Cifrado</div>
                  <div class="text-muted-foreground">Datos seguros</div>
                </div>
              </div>
              <p class="text-xs text-muted-foreground mt-6">
                Tus datos se utilizarán exclusivamente para personalizar tu experiencia
                y para fines de investigación universitaria de forma anónima.
              </p>
            </div>
          `
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 1: Datos sociodemográficos (10 campos)
    // =========================================================================
    {
      name: "datos_sociodemograficos",
      navigationTitle: "👤 Sobre ti",
      title: "Cuéntanos sobre ti",
      description: "Información básica para personalizar tu experiencia",
      elements: [
        {
          type: "text",
          name: "nombre",
          title: "¿Cómo te gustaría que me dirija a ti?",
          placeholder: "Tu nombre o apodo",
          isRequired: true,
          maxLength: 50
        },
        {
          type: "text",
          name: "edad",
          title: "¿Cuántos años tienes?",
          inputType: "number",
          min: 18,
          max: 100,
          isRequired: true,
          validators: [
            { type: "numeric", minValue: 18, maxValue: 100, text: "Por favor, introduce una edad válida (18-100)" }
          ]
        },
        {
          type: "dropdown",
          name: "nivel_educativo",
          title: "¿Cuál es tu nivel de estudios?",
          isRequired: true,
          choices: [
            { value: "sin_estudios", text: "Sin estudios formales" },
            { value: "primaria", text: "Educación primaria" },
            { value: "secundaria", text: "Educación secundaria (ESO)" },
            { value: "bachillerato_fp", text: "Bachillerato o FP" },
            { value: "universidad", text: "Estudios universitarios" },
            { value: "postgrado", text: "Máster o Postgrado" },
            { value: "doctorado", text: "Doctorado" }
          ]
        },
        {
          type: "dropdown",
          name: "situacion_laboral",
          title: "¿Cuál es tu situación laboral actual?",
          isRequired: true,
          choices: [
            { value: "empleada_completo", text: "Empleada a tiempo completo" },
            { value: "empleada_parcial", text: "Empleada a tiempo parcial" },
            { value: "autonoma", text: "Autónoma / Empresaria" },
            { value: "desempleada", text: "Desempleada" },
            { value: "estudiante", text: "Estudiante" },
            { value: "jubilada", text: "Jubilada" },
            { value: "hogar", text: "Trabajo doméstico no remunerado" }
          ]
        },
        {
          type: "dropdown",
          name: "estado_civil",
          title: "¿Cuál es tu estado civil?",
          isRequired: true,
          choices: [
            { value: "soltera", text: "Soltera" },
            { value: "pareja", text: "En pareja / Casada" },
            { value: "separada", text: "Separada / Divorciada" },
            { value: "viuda", text: "Viuda" }
          ]
        },
        {
          type: "radiogroup",
          name: "tiene_hijos",
          title: "¿Tienes hijos?",
          isRequired: true,
          choices: [
            { value: "si", text: "Sí" },
            { value: "no", text: "No" }
          ]
        },
        {
          type: "text",
          name: "numero_hijos",
          title: "¿Cuántos hijos tienes?",
          visibleIf: "{tiene_hijos} = 'si'",
          inputType: "number",
          min: 1,
          max: 15,
          isRequired: true,
          requiredIf: "{tiene_hijos} = 'si'"
        },
        {
          type: "text",
          name: "edades_hijos",
          title: "¿Qué edades tienen tus hijos?",
          description: "Separa las edades con comas (ej: 5, 8, 12)",
          visibleIf: "{tiene_hijos} = 'si'",
          placeholder: "Ej: 5, 8, 12"
        },
        {
          type: "checkbox",
          name: "cuidadora",
          title: "¿Eres la principal cuidadora de alguna persona dependiente?",
          choices: [
            { value: "no", text: "No" },
            { value: "ninos", text: "Sí, de niños" },
            { value: "mayores", text: "Sí, de personas mayores" },
            { value: "discapacidad", text: "Sí, de personas con discapacidad" }
          ]
        },
        {
          type: "dropdown",
          name: "horas_cuidado",
          title: "¿Cuántas horas semanales dedicas al cuidado?",
          visibleIf: "{cuidadora} contains 'ninos' or {cuidadora} contains 'mayores' or {cuidadora} contains 'discapacidad'",
          choices: [
            { value: "menos_10", text: "Menos de 10 horas" },
            { value: "10_20", text: "10-20 horas" },
            { value: "20_40", text: "20-40 horas" },
            { value: "mas_40", text: "Más de 40 horas" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 2: Estado de salud general (4 campos)
    // =========================================================================
    {
      name: "salud_general",
      navigationTitle: "❤️ Salud general",
      title: "Tu estado de salud",
      description: "Información general sobre tu salud",
      elements: [
        {
          type: "rating",
          name: "estado_salud",
          title: "En general, ¿cómo describirías tu estado de salud?",
          isRequired: true,
          rateValues: [
            { value: 1, text: "Malo" },
            { value: 2, text: "Regular" },
            { value: 3, text: "Bueno" },
            { value: 4, text: "Muy bueno" },
            { value: 5, text: "Excelente" }
          ]
        },
        {
          type: "checkbox",
          name: "condiciones_diagnosticadas",
          title: "¿Has sido diagnosticada con alguna de estas condiciones?",
          description: "Selecciona todas las que apliquen",
          choices: [
            { value: "ninguna", text: "Ninguna de las anteriores" },
            { value: "hipertension", text: "Hipertensión arterial" },
            { value: "diabetes", text: "Diabetes" },
            { value: "cardiovascular", text: "Enfermedades cardiovasculares" },
            { value: "respiratorias", text: "Enfermedades respiratorias crónicas" },
            { value: "cancer", text: "Cáncer" },
            { value: "autoinmunes", text: "Enfermedades autoinmunes" },
            { value: "tiroides", text: "Trastornos tiroideos" },
            { value: "osteoporosis", text: "Osteoporosis" },
            { value: "anemia", text: "Anemia" },
            { value: "migrana", text: "Migraña / Cefaleas crónicas" }
          ]
        },
        {
          type: "comment",
          name: "medicamentos",
          title: "¿Tomas algún medicamento de forma regular?",
          description: "Incluye nombre del medicamento y para qué lo tomas. Escribe 'Ninguno' si no tomas medicamentos.",
          isRequired: true,
          placeholder: "Ej: Eutirox (tiroides), Ibuprofeno (dolor menstrual)... o 'Ninguno'",
          rows: 3
        },
        {
          type: "radiogroup",
          name: "tiene_alergias",
          title: "¿Tienes alguna alergia o intolerancia conocida?",
          description: "Incluye alergias a medicamentos, alimentos, ambientales, etc.",
          isRequired: true,
          choices: [
            { value: "no", text: "No" },
            { value: "si", text: "Sí" }
          ]
        },
        {
          type: "comment",
          name: "alergias_detalle",
          title: "¿Cuáles son tus alergias o intolerancias?",
          visibleIf: "{tiene_alergias} = 'si'",
          isRequired: true,
          requiredIf: "{tiene_alergias} = 'si'",
          placeholder: "Sepáralas con comas (ej: penicilina, mariscos, látex)",
          rows: 2,
          validators: [
            {
              type: "text",
              minLength: 3,
              text: "Por favor, especifica tus alergias (mínimo 3 caracteres)"
            }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 3: Salud reproductiva y ginecológica (9 campos)
    // =========================================================================
    {
      name: "salud_reproductiva",
      navigationTitle: "🌸 Salud femenina",
      title: "Salud reproductiva y ginecológica",
      description: "Información sobre tu salud ginecológica",
      elements: [
        {
          type: "dropdown",
          name: "etapa_ciclo",
          title: "¿En qué etapa del ciclo vital te encuentras?",
          isRequired: true,
          choices: [
            { value: "fertil_regular", text: "Edad fértil con menstruación regular" },
            { value: "fertil_irregular", text: "Edad fértil con menstruación irregular" },
            { value: "perimenopausia", text: "Perimenopausia o climaterio" },
            { value: "menopausia", text: "Menopausia" },
            { value: "postmenopausia", text: "Postmenopausia" }
          ]
        },
        {
          type: "dropdown",
          name: "revisiones_ginecologicas",
          title: "¿Con qué frecuencia acudes a revisiones ginecológicas?",
          isRequired: true,
          choices: [
            { value: "anual", text: "Anualmente" },
            { value: "2_3_anos", text: "Cada 2-3 años" },
            { value: "sintomas", text: "Solo cuando tengo síntomas" },
            { value: "nunca", text: "Nunca o casi nunca" }
          ]
        },
        {
          type: "dropdown",
          name: "mamografia",
          title: "¿Te has realizado una mamografía en los últimos 2 años?",
          description: "Recomendada a partir de los 50 años, o antes si hay factores de riesgo",
          isRequired: true,
          choices: [
            { value: "si_reciente", text: "Sí, en los últimos 2 años" },
            { value: "si_antigua", text: "Sí, pero hace más de 2 años" },
            { value: "nunca", text: "Nunca me la he hecho" },
            { value: "no_aplica", text: "No aplica por mi edad o situación" }
          ]
        },
        {
          type: "dropdown",
          name: "papanicolaou",
          title: "¿Te has realizado una citología (Papanicolaou) en los últimos 3 años?",
          description: "Recomendada cada 3 años entre los 25 y 65 años",
          isRequired: true,
          choices: [
            { value: "si_reciente", text: "Sí, en los últimos 3 años" },
            { value: "si_antigua", text: "Sí, pero hace más de 3 años" },
            { value: "nunca", text: "Nunca me la he hecho" },
            { value: "no_aplica", text: "No aplica por mi edad o situación" }
          ]
        },
        {
          type: "dropdown",
          name: "situacion_reproductiva",
          title: "¿Te encuentras en alguna de estas situaciones?",
          choices: [
            { value: "ninguna", text: "No me encuentro en ninguna de estas" },
            { value: "embarazada", text: "Estoy embarazada" },
            { value: "puerperio", text: "Estoy en el puerperio (hasta 6 meses tras el parto)" },
            { value: "lactancia", text: "Estoy en período de lactancia" },
            { value: "buscando_embarazo", text: "Estoy intentando quedarme embarazada" }
          ]
        },
        {
          type: "rating",
          name: "salud_etapa",
          title: "¿Cómo valorarías tu salud en tu etapa actual (ciclo menstrual, menopausia...)?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy mala",
          maxRateDescription: "Muy buena"
        },
        {
          type: "checkbox",
          name: "sintomas_menstruacion",
          title: "Si tienes la menstruación, ¿qué síntomas sueles tener?",
          visibleIf: "{etapa_ciclo} = 'fertil_regular' or {etapa_ciclo} = 'fertil_irregular'",
          choices: [
            { value: "ninguno", text: "No tengo síntomas relevantes" },
            { value: "fatiga", text: "Cansancio o fatiga" },
            { value: "dolor_abdominal", text: "Dolor abdominal o pélvico" },
            { value: "migrana", text: "Migrañas o dolor de cabeza" },
            { value: "dolor_mamas", text: "Dolor en mamas" },
            { value: "hinchazon", text: "Hinchazón o retención de líquidos" },
            { value: "cambios_humor", text: "Cambios de humor o irritabilidad" },
            { value: "concentracion", text: "Dificultad para concentrarse" },
            { value: "nauseas", text: "Náuseas" }
          ]
        },
        {
          type: "rating",
          name: "afectacion_menstruacion",
          title: "¿Cuánto afecta la menstruación a tu vida diaria?",
          visibleIf: "{etapa_ciclo} = 'fertil_regular' or {etapa_ciclo} = 'fertil_irregular'",
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Nada",
          maxRateDescription: "Muchísimo"
        },
        {
          type: "checkbox",
          name: "medidas_alivio",
          title: "¿Qué medidas usas para aliviar los síntomas menstruales?",
          visibleIf: "({etapa_ciclo} = 'fertil_regular' or {etapa_ciclo} = 'fertil_irregular') and {sintomas_menstruacion} notcontains 'ninguno'",
          choices: [
            { value: "ninguna", text: "No uso ninguna medida" },
            { value: "analgesicos", text: "Analgésicos (ibuprofeno, paracetamol...)" },
            { value: "calor", text: "Calor local (bolsa de agua caliente)" },
            { value: "ejercicio", text: "Ejercicio físico" },
            { value: "descanso", text: "Descanso" },
            { value: "anticonceptivos", text: "Anticonceptivos hormonales" },
            { value: "remedios_naturales", text: "Remedios naturales (infusiones, suplementos...)" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 4: Salud mental y emocional - PHQ-4 completo (6 campos)
    // =========================================================================
    {
      name: "salud_mental",
      navigationTitle: "🧠 Bienestar emocional",
      title: "Tu bienestar emocional",
      description: "Las siguientes preguntas forman parte del cuestionario PHQ-4, una herramienta validada científicamente.",
      elements: [
        {
          type: "html",
          name: "phq4_intro",
          html: `
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p class="text-sm text-blue-800">
                <strong>Sobre estas preguntas:</strong> Las siguientes 4 preguntas son parte del
                cuestionario PHQ-4, utilizado en investigación para evaluar el bienestar emocional.
                No hay respuestas correctas o incorrectas. Responde según cómo te has sentido
                en las <strong>últimas 2 semanas</strong>.
              </p>
            </div>
          `
        },
        {
          type: "rating",
          name: "poco_interes",
          title: "En las últimas 2 semanas, ¿con qué frecuencia has sentido poco interés o placer en hacer cosas?",
          isRequired: true,
          rateValues: [
            { value: 0, text: "Nunca" },
            { value: 1, text: "Algunos días" },
            { value: 2, text: "Más de la mitad de los días" },
            { value: 3, text: "Casi todos los días" }
          ]
        },
        {
          type: "rating",
          name: "decaida",
          title: "En las últimas 2 semanas, ¿con qué frecuencia te has sentido decaída, deprimida o sin esperanza?",
          isRequired: true,
          rateValues: [
            { value: 0, text: "Nunca" },
            { value: 1, text: "Algunos días" },
            { value: 2, text: "Más de la mitad de los días" },
            { value: 3, text: "Casi todos los días" }
          ]
        },
        {
          type: "rating",
          name: "nerviosa",
          title: "En las últimas 2 semanas, ¿con qué frecuencia te has sentido nerviosa, ansiosa o con los nervios de punta?",
          isRequired: true,
          rateValues: [
            { value: 0, text: "Nunca" },
            { value: 1, text: "Algunos días" },
            { value: 2, text: "Más de la mitad de los días" },
            { value: 3, text: "Casi todos los días" }
          ]
        },
        {
          type: "rating",
          name: "preocupaciones",
          title: "En las últimas 2 semanas, ¿con qué frecuencia no has podido dejar de preocuparte o controlar tus preocupaciones?",
          isRequired: true,
          rateValues: [
            { value: 0, text: "Nunca" },
            { value: 1, text: "Algunos días" },
            { value: 2, text: "Más de la mitad de los días" },
            { value: 3, text: "Casi todos los días" }
          ]
        },
        {
          type: "rating",
          name: "nivel_estres",
          title: "¿Cómo calificarías tu nivel de estrés habitual?",
          isRequired: true,
          rateValues: [
            { value: 1, text: "Muy bajo" },
            { value: 2, text: "Bajo" },
            { value: 3, text: "Moderado" },
            { value: 4, text: "Alto" },
            { value: 5, text: "Muy alto" }
          ]
        },
        {
          type: "dropdown",
          name: "tiempo_autocuidado",
          title: "¿Dispones de tiempo para actividades de autocuidado y bienestar personal?",
          isRequired: true,
          choices: [
            { value: "regularmente", text: "Sí, regularmente" },
            { value: "ocasionalmente", text: "Sí, ocasionalmente" },
            { value: "rara_vez", text: "Rara vez" },
            { value: "nunca", text: "Nunca" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 5: Hábitos de vida y alimentación (12 campos)
    // =========================================================================
    {
      name: "habitos_vida",
      navigationTitle: "🥗 Hábitos de vida",
      title: "Tus hábitos diarios",
      description: "Actividad física, alimentación y descanso",
      elements: [
        {
          type: "dropdown",
          name: "frecuencia_actividad",
          title: "¿Con qué frecuencia realizas actividad física moderada?",
          description: "Caminar rápido, nadar, bailar, etc.",
          isRequired: true,
          choices: [
            { value: "diariamente", text: "Diariamente" },
            { value: "3_5_semana", text: "3-5 veces por semana" },
            { value: "1_2_semana", text: "1-2 veces por semana" },
            { value: "ocasionalmente", text: "Ocasionalmente" },
            { value: "nunca", text: "Nunca o casi nunca" }
          ]
        },
        {
          type: "dropdown",
          name: "minutos_actividad",
          title: "Cuando haces ejercicio, ¿cuántos minutos sueles dedicarle?",
          visibleIf: "{frecuencia_actividad} != 'nunca'",
          choices: [
            { value: "menos_15", text: "Menos de 15 minutos" },
            { value: "15_30", text: "15-30 minutos" },
            { value: "30_60", text: "30-60 minutos" },
            { value: "mas_60", text: "Más de 60 minutos" }
          ]
        },
        {
          type: "dropdown",
          name: "raciones_frutas",
          title: "¿Cuántas raciones de frutas y verduras consumes al día?",
          isRequired: true,
          choices: [
            { value: "ninguna", text: "Ninguna o casi ninguna" },
            { value: "1_2", text: "1-2 raciones" },
            { value: "3_4", text: "3-4 raciones" },
            { value: "5_mas", text: "5 o más raciones" }
          ]
        },
        {
          type: "dropdown",
          name: "consumo_legumbres",
          title: "¿Con qué frecuencia consumes legumbres?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "Nunca o casi nunca" },
            { value: "1_mes", text: "1-2 veces al mes" },
            { value: "1_semana", text: "1 vez por semana" },
            { value: "2_3_semana", text: "2-3 veces por semana" },
            { value: "mas_3_semana", text: "Más de 3 veces por semana" }
          ]
        },
        {
          type: "dropdown",
          name: "consumo_pescado",
          title: "¿Con qué frecuencia consumes pescado?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "Nunca o casi nunca" },
            { value: "1_mes", text: "1-2 veces al mes" },
            { value: "1_semana", text: "1 vez por semana" },
            { value: "2_3_semana", text: "2-3 veces por semana" },
            { value: "mas_3_semana", text: "Más de 3 veces por semana" }
          ]
        },
        {
          type: "dropdown",
          name: "consumo_carnes",
          title: "¿Con qué frecuencia consumes carnes procesadas (embutidos, salchichas...)?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "Nunca o casi nunca" },
            { value: "1_mes", text: "1-2 veces al mes" },
            { value: "1_semana", text: "1 vez por semana" },
            { value: "2_3_semana", text: "2-3 veces por semana" },
            { value: "diariamente", text: "Casi todos los días" }
          ]
        },
        {
          type: "dropdown",
          name: "consumo_dulces",
          title: "¿Con qué frecuencia consumes dulces, bollería o refrescos azucarados?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "Nunca o casi nunca" },
            { value: "1_mes", text: "1-2 veces al mes" },
            { value: "1_semana", text: "1 vez por semana" },
            { value: "2_3_semana", text: "2-3 veces por semana" },
            { value: "diariamente", text: "Casi todos los días" }
          ]
        },
        {
          type: "dropdown",
          name: "vasos_agua",
          title: "¿Cuántos vasos de agua bebes al día?",
          isRequired: true,
          choices: [
            { value: "menos_3", text: "Menos de 3" },
            { value: "3_4", text: "3-4" },
            { value: "5_7", text: "5-7" },
            { value: "8_mas", text: "8 o más" }
          ]
        },
        {
          type: "dropdown",
          name: "consumo_alcohol",
          title: "¿Consumes alcohol?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "No, nunca" },
            { value: "ocasional", text: "Sí, ocasionalmente" },
            { value: "semanal", text: "Sí, semanalmente" },
            { value: "frecuente", text: "Sí, varias veces por semana" },
            { value: "diario", text: "Sí, diariamente" }
          ]
        },
        {
          type: "dropdown",
          name: "fumar",
          title: "¿Fumas?",
          isRequired: true,
          choices: [
            { value: "nunca", text: "No, nunca he fumado" },
            { value: "exfumadora", text: "No, soy ex-fumadora" },
            { value: "ocasional", text: "Sí, ocasionalmente" },
            { value: "diario", text: "Sí, diariamente" }
          ]
        },
        {
          type: "dropdown",
          name: "horas_sueno",
          title: "¿Cuántas horas duermes habitualmente por noche?",
          isRequired: true,
          choices: [
            { value: "menos_5", text: "Menos de 5 horas" },
            { value: "5_6", text: "5-6 horas" },
            { value: "6_7", text: "6-7 horas" },
            { value: "7_8", text: "7-8 horas" },
            { value: "mas_8", text: "Más de 8 horas" }
          ]
        },
        {
          type: "rating",
          name: "calidad_sueno",
          title: "¿Cómo calificarías la calidad de tu sueño?",
          isRequired: true,
          rateValues: [
            { value: 1, text: "Muy mala" },
            { value: 2, text: "Mala" },
            { value: 3, text: "Regular" },
            { value: 4, text: "Buena" },
            { value: 5, text: "Muy buena" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 6: Condiciones laborales y sociofamiliares (5 campos)
    // =========================================================================
    {
      name: "condiciones_laborales",
      navigationTitle: "💼 Trabajo y apoyo",
      title: "Tu entorno laboral y social",
      description: "Condiciones de trabajo y red de apoyo",
      visibleIf: "{situacion_laboral} = 'empleada_completo' or {situacion_laboral} = 'empleada_parcial' or {situacion_laboral} = 'autonoma'",
      elements: [
        {
          type: "rating",
          name: "satisfaccion_trabajo",
          title: "¿Cómo de satisfecha estás con tu trabajo actual?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy insatisfecha",
          maxRateDescription: "Muy satisfecha"
        },
        {
          type: "boolean",
          name: "trabajo_afecta_salud",
          title: "¿Sientes que tu trabajo afecta negativamente a tu salud?",
          isRequired: true,
          labelTrue: "Sí",
          labelFalse: "No"
        },
        {
          type: "rating",
          name: "conciliacion",
          title: "¿Cómo valoras tu conciliación vida laboral-personal?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy mala",
          maxRateDescription: "Excelente"
        },
        {
          type: "rating",
          name: "apoyo_familiar",
          title: "¿Cuentas con apoyo familiar para las tareas del hogar y cuidados?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Ningún apoyo",
          maxRateDescription: "Mucho apoyo"
        },
        {
          type: "rating",
          name: "red_apoyo",
          title: "¿Sientes que tienes una buena red de apoyo (familia, amigos)?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy poca",
          maxRateDescription: "Muy amplia"
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 7: Estudios (7 campos) - Solo para estudiantes
    // =========================================================================
    {
      name: "estudios",
      navigationTitle: "📚 Estudios",
      title: "Tus estudios",
      description: "Información sobre tu situación académica",
      visibleIf: "{situacion_laboral} = 'estudiante'",
      elements: [
        {
          type: "dropdown",
          name: "tipo_estudios",
          title: "¿Qué tipo de estudios estás cursando?",
          isRequired: true,
          choices: [
            { value: "secundaria", text: "Educación secundaria (ESO)" },
            { value: "bachillerato", text: "Bachillerato" },
            { value: "fp_medio", text: "FP Grado Medio" },
            { value: "fp_superior", text: "FP Grado Superior" },
            { value: "grado", text: "Grado universitario" },
            { value: "master", text: "Máster" },
            { value: "doctorado", text: "Doctorado" },
            { value: "otro", text: "Otros estudios" }
          ]
        },
        {
          type: "rating",
          name: "satisfaccion_estudios",
          title: "¿Cómo de satisfecha estás con tus estudios?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy insatisfecha",
          maxRateDescription: "Muy satisfecha"
        },
        {
          type: "boolean",
          name: "estudios_afectan_salud",
          title: "¿Sientes que tus estudios afectan negativamente a tu salud?",
          isRequired: true,
          labelTrue: "Sí",
          labelFalse: "No"
        },
        {
          type: "dropdown",
          name: "horas_estudio",
          title: "¿Cuántas horas semanales dedicas al estudio (fuera de clase)?",
          isRequired: true,
          choices: [
            { value: "menos_5", text: "Menos de 5 horas" },
            { value: "5_10", text: "5-10 horas" },
            { value: "10_20", text: "10-20 horas" },
            { value: "20_30", text: "20-30 horas" },
            { value: "mas_30", text: "Más de 30 horas" }
          ]
        },
        {
          type: "rating",
          name: "estres_estudios",
          title: "¿Cuánto estrés te generan los estudios?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy poco",
          maxRateDescription: "Muchísimo"
        },
        {
          type: "boolean",
          name: "tiempo_descanso",
          title: "¿Sientes que tienes tiempo suficiente para descansar?",
          isRequired: true,
          labelTrue: "Sí",
          labelFalse: "No"
        },
        {
          type: "rating",
          name: "apoyo_estudios",
          title: "¿Cuentas con apoyo (familiar, económico, emocional) durante tus estudios?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Ningún apoyo",
          maxRateDescription: "Mucho apoyo"
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 8: Hobbies e intereses (3 campos)
    // =========================================================================
    {
      name: "hobbies",
      navigationTitle: "🎨 Tiempo libre",
      title: "Tus hobbies e intereses",
      description: "Actividades que disfrutas en tu tiempo libre",
      elements: [
        {
          type: "checkbox",
          name: "actividades_tiempo_libre",
          title: "¿Qué actividades disfrutas en tu tiempo libre?",
          description: "Selecciona todas las que apliquen",
          isRequired: true,
          choices: [
            { value: "lectura", text: "Lectura" },
            { value: "musica", text: "Música" },
            { value: "cine_series", text: "Cine y series" },
            { value: "deporte", text: "Deportes" },
            { value: "manualidades", text: "Manualidades y artesanía" },
            { value: "jardineria", text: "Jardinería" },
            { value: "cocina", text: "Cocina" },
            { value: "viajes", text: "Viajar" },
            { value: "voluntariado", text: "Voluntariado" },
            { value: "redes_sociales", text: "Redes sociales" },
            { value: "videojuegos", text: "Videojuegos" },
            { value: "otro", text: "Otras actividades" }
          ]
        },
        {
          type: "dropdown",
          name: "frecuencia_hobbies",
          title: "¿Con qué frecuencia dedicas tiempo a tus hobbies?",
          isRequired: true,
          choices: [
            { value: "diariamente", text: "Diariamente" },
            { value: "varias_semana", text: "Varias veces por semana" },
            { value: "una_semana", text: "Una vez por semana" },
            { value: "ocasionalmente", text: "Ocasionalmente" },
            { value: "nunca", text: "Nunca tengo tiempo" }
          ]
        },
        {
          type: "comment",
          name: "actividades_deseadas",
          title: "¿Hay alguna actividad que te gustaría incorporar a tu rutina?",
          placeholder: "Ej: yoga, pintura, aprender idiomas...",
          rows: 2
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 9: Uso de tecnologías para la salud (4 campos)
    // =========================================================================
    {
      name: "tecnologias_salud",
      navigationTitle: "📱 Tecnología y salud",
      title: "Tecnología y salud",
      description: "Cómo usas la tecnología para cuidar tu salud",
      elements: [
        {
          type: "checkbox",
          name: "apps_salud",
          title: "¿Utilizas aplicaciones o dispositivos para monitorear tu salud?",
          description: "Selecciona todas las que uses",
          choices: [
            { value: "ninguna", text: "No uso ninguna" },
            { value: "ciclo_menstrual", text: "Apps de seguimiento del ciclo menstrual" },
            { value: "actividad_fisica", text: "Apps de actividad física / podómetro" },
            { value: "alimentacion", text: "Apps de alimentación o conteo de calorías" },
            { value: "meditacion", text: "Apps de meditación o mindfulness" },
            { value: "sueno", text: "Apps de seguimiento del sueño" },
            { value: "smartwatch", text: "Smartwatch o pulsera de actividad" }
          ]
        },
        {
          type: "dropdown",
          name: "busqueda_informacion",
          title: "Cuando tienes una duda de salud, ¿dónde sueles buscar información primero?",
          isRequired: true,
          choices: [
            { value: "internet_general", text: "Internet (Google, etc.)" },
            { value: "medico", text: "Consulto a mi médico/a" },
            { value: "farmacia", text: "Pregunto en la farmacia" },
            { value: "redes_sociales", text: "Redes sociales" },
            { value: "familia_amigos", text: "Familia y amigos" },
            { value: "webs_oficiales", text: "Webs oficiales de salud" },
            { value: "no_busco", text: "No suelo buscar información" }
          ]
        },
        {
          type: "rating",
          name: "confianza_informacion",
          title: "¿Cuánto confías en la información de salud que encuentras online?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Nada",
          maxRateDescription: "Mucho"
        },
        {
          type: "dropdown",
          name: "telemedicina",
          title: "¿Has utilizado servicios de telemedicina (consultas online)?",
          isRequired: true,
          choices: [
            { value: "si_frecuente", text: "Sí, habitualmente" },
            { value: "si_alguna_vez", text: "Sí, alguna vez" },
            { value: "no_pero_interesa", text: "No, pero me interesaría" },
            { value: "no_prefiero_presencial", text: "No, prefiero consultas presenciales" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 10: Empoderamiento en salud (11 campos)
    // =========================================================================
    {
      name: "empoderamiento",
      navigationTitle: "💪 Empoderamiento",
      title: "Tu empoderamiento en salud",
      description: "Conocimiento y participación activa en tu salud",
      elements: [
        {
          type: "rating",
          name: "conocimiento_salud",
          title: "¿Cómo valorarías tu conocimiento sobre salud femenina?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Muy bajo",
          maxRateDescription: "Muy alto"
        },
        {
          type: "rating",
          name: "decisiones_informadas",
          title: "¿Te sientes capaz de tomar decisiones informadas sobre tu salud?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Nada capaz",
          maxRateDescription: "Muy capaz"
        },
        {
          type: "rating",
          name: "control_factores",
          title: "¿Sientes que tienes control sobre los factores que afectan a tu salud?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Ningún control",
          maxRateDescription: "Total control"
        },
        {
          type: "checkbox",
          name: "aspectos_mejorar",
          title: "¿Qué aspectos de tu salud te gustaría mejorar?",
          description: "Selecciona los más importantes para ti",
          choices: [
            { value: "alimentacion", text: "Alimentación" },
            { value: "ejercicio", text: "Actividad física" },
            { value: "sueno", text: "Calidad del sueño" },
            { value: "estres", text: "Gestión del estrés" },
            { value: "salud_mental", text: "Salud mental y emocional" },
            { value: "salud_ginecologica", text: "Salud ginecológica" },
            { value: "peso", text: "Peso corporal" },
            { value: "energia", text: "Niveles de energía" },
            { value: "ninguno", text: "Estoy satisfecha con mi salud actual" }
          ]
        },
        {
          type: "radiogroup",
          name: "conoce_recursos",
          title: "¿Conoces los recursos públicos de salud disponibles en tu zona?",
          isRequired: true,
          choices: [
            { value: "si_bien", text: "Sí, los conozco bien" },
            { value: "si_algunos", text: "Sí, conozco algunos" },
            { value: "no_pero_interesa", text: "No, pero me gustaría conocerlos" },
            { value: "no", text: "No los conozco" }
          ]
        },
        {
          type: "radiogroup",
          name: "conoce_recursos_murcia",
          title: "¿Conoces recursos específicos de salud para mujeres en la Región de Murcia?",
          isRequired: true,
          choices: [
            { value: "si_varios", text: "Sí, conozco varios" },
            { value: "si_alguno", text: "Sí, conozco alguno" },
            { value: "no", text: "No los conozco" }
          ]
        },
        {
          type: "radiogroup",
          name: "participacion_salud",
          title: "¿Participas o has participado en actividades de salud comunitaria?",
          description: "Talleres, grupos de apoyo, campañas...",
          isRequired: true,
          choices: [
            { value: "si_actualmente", text: "Sí, actualmente" },
            { value: "si_pasado", text: "Sí, en el pasado" },
            { value: "no_pero_interesa", text: "No, pero me interesaría" },
            { value: "no", text: "No" }
          ]
        },
        {
          type: "rating",
          name: "capacidad_propuestas",
          title: "¿Te sientes con capacidad de proponer mejoras en servicios de salud?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Nada capaz",
          maxRateDescription: "Muy capaz"
        },
        {
          type: "rating",
          name: "conciencia_problemas",
          title: "¿Eres consciente de los problemas de salud específicos de las mujeres?",
          isRequired: true,
          rateMin: 1,
          rateMax: 5,
          minRateDescription: "Nada consciente",
          maxRateDescription: "Muy consciente"
        },
        {
          type: "radiogroup",
          name: "contribucion_salud",
          title: "¿Te gustaría contribuir a mejorar la salud de las mujeres?",
          isRequired: true,
          choices: [
            { value: "si_mucho", text: "Sí, me gustaría mucho" },
            { value: "si_algo", text: "Sí, de alguna manera" },
            { value: "no_segura", text: "No estoy segura" },
            { value: "no", text: "No especialmente" }
          ]
        },
        {
          type: "checkbox",
          name: "involucracion_salud",
          title: "¿Cómo te gustaría involucrarte en la promoción de la salud de las mujeres?",
          description: "Selecciona todas las opciones que te interesen",
          choices: [
            { value: "informacion", text: "Compartiendo información" },
            { value: "experiencias", text: "Compartiendo mis experiencias" },
            { value: "voluntariado", text: "Como voluntaria" },
            { value: "talleres", text: "Participando en talleres" },
            { value: "grupos_apoyo", text: "En grupos de apoyo" },
            { value: "redes_sociales", text: "A través de redes sociales" },
            { value: "ninguna", text: "No me interesa involucrarme activamente" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 11: Expectativas sobre el agente virtual (5 campos)
    // =========================================================================
    {
      name: "expectativas",
      navigationTitle: "🤖 Sobre MarIA",
      title: "Tus expectativas sobre MarIA",
      description: "Ayúdanos a entender qué esperas de este asistente",
      elements: [
        {
          type: "checkbox",
          name: "expectativas_agente",
          title: "¿Qué esperas de un agente virtual de salud como MarIA?",
          description: "Selecciona todas las que apliquen",
          isRequired: true,
          choices: [
            { value: "informacion_fiable", text: "Información fiable sobre salud" },
            { value: "recomendaciones_personalizadas", text: "Recomendaciones personalizadas" },
            { value: "recordatorios", text: "Recordatorios de revisiones y citas" },
            { value: "seguimiento_habitos", text: "Seguimiento de hábitos saludables" },
            { value: "respuestas_dudas", text: "Respuestas a mis dudas de salud" },
            { value: "apoyo_emocional", text: "Apoyo emocional" },
            { value: "derivacion_profesionales", text: "Derivación a profesionales cuando sea necesario" }
          ]
        },
        {
          type: "checkbox",
          name: "temas_prioritarios",
          title: "¿Qué temas de salud te gustaría que MarIA priorizara?",
          description: "Selecciona hasta 3 opciones",
          isRequired: true,
          maxSelectedChoices: 3,
          choices: [
            { value: "nutricion", text: "Nutrición y alimentación" },
            { value: "ejercicio", text: "Ejercicio físico" },
            { value: "salud_mental", text: "Salud mental y emocional" },
            { value: "salud_reproductiva", text: "Salud reproductiva" },
            { value: "menopausia", text: "Menopausia y climaterio" },
            { value: "prevencion", text: "Prevención y detección precoz" },
            { value: "enfermedades_cronicas", text: "Enfermedades crónicas" },
            { value: "sueno", text: "Calidad del sueño" },
            { value: "relaciones", text: "Relaciones y sexualidad" }
          ]
        },
        {
          type: "dropdown",
          name: "frecuencia_uso",
          title: "¿Con qué frecuencia crees que usarías MarIA?",
          isRequired: true,
          choices: [
            { value: "diariamente", text: "Diariamente" },
            { value: "varias_semana", text: "Varias veces por semana" },
            { value: "semanalmente", text: "Una vez por semana" },
            { value: "ocasionalmente", text: "Ocasionalmente" },
            { value: "no_segura", text: "No estoy segura" }
          ]
        },
        {
          type: "checkbox",
          name: "preocupaciones_uso",
          title: "¿Tienes alguna preocupación sobre usar un asistente virtual de salud?",
          choices: [
            { value: "ninguna", text: "No tengo preocupaciones" },
            { value: "privacidad", text: "Privacidad de mis datos" },
            { value: "fiabilidad", text: "Fiabilidad de la información" },
            { value: "sustituir_medico", text: "Que sustituya la atención médica real" },
            { value: "complejidad", text: "Que sea difícil de usar" },
            { value: "impersonal", text: "Que sea muy impersonal" }
          ]
        },
        {
          type: "radiogroup",
          name: "utilidad_directorio",
          title: "¿Te resultaría útil un directorio de recursos de salud para mujeres en tu zona?",
          isRequired: true,
          choices: [
            { value: "muy_util", text: "Sí, muy útil" },
            { value: "util", text: "Sí, algo útil" },
            { value: "poco_util", text: "Poco útil" },
            { value: "no_util", text: "No me sería útil" }
          ]
        }
      ]
    },

    // =========================================================================
    // SECCIÓN 12: Comentarios adicionales (1 campo)
    // =========================================================================
    {
      name: "comentarios_adicionales",
      navigationTitle: "💬 Comentarios",
      title: "¿Algo más que quieras compartir?",
      description: "Espacio libre para cualquier información adicional",
      elements: [
        {
          type: "comment",
          name: "comentarios",
          title: "¿Hay algo más que quieras contarnos sobre tu salud o sobre lo que esperas de MarIA?",
          description: "Este espacio es opcional. Puedes compartir cualquier información que consideres relevante.",
          placeholder: "Escribe aquí si lo deseas...",
          rows: 4
        }
      ]
    }
  ]
};

/**
 * Exportación por defecto para compatibilidad con imports existentes
 */
export default surveyJson;

/**
 * Conteo de campos por categoría (debe coincidir con encuesta.py)
 *
 * TOTAL: 77 campos
 *
 * - datos_sociodemograficos: 10
 * - salud_general: 4
 * - salud_reproductiva: 9
 * - salud_mental: 6 (PHQ-4 completo)
 * - habitos_vida: 12
 * - condiciones_laborales: 5
 * - estudios: 7
 * - hobbies: 3
 * - tecnologias_salud: 4
 * - empoderamiento: 11
 * - expectativas: 5
 * - comentarios: 1
 */
